// index.tsx
// (c) NCR Voyix Corporation 2025. All rights reserved.

import React, { useCallback, useEffect, useState } from 'react'
import ExternalRedirectModal from '../ExternalRedirectModal'

export interface ExternalRedirectProviderProps {
  /** Child react nodes (your entire app) */
  children: React.ReactNode
  /** Domains that do not need warning */
  whitelist?: string[]
  /** Should the destination open in new tab (default: true) */
  openInNewTab?: boolean
}

/**
 * Global click‑interceptor that pops ExternalRedirectModal whenever the user
 * tries to leave the Upgrades UI for a non‑whitelisted origin.
 */
export default function ExternalRedirectProvider({
  children,
  whitelist = [window.location.hostname],
  openInNewTab = true
}: ExternalRedirectProviderProps) : JSX.Element {
  const [pendingUrl, setPendingUrl] = useState<string | null>(null)

  const isAllowed = useCallback(
    (href: string) => {
      try {
        const url = new URL(href, window.location.href)
        // Internal nav → allow. Whitelisted domains → allow.
        if (url.origin === window.location.origin) return true
        return whitelist.some((d) => url.hostname.endsWith(d))
      } catch (err) {
        // If URL cannot be parsed, play safe and allow (likely a hash link)
        return true
      }
    },
    [whitelist]
  )

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return

      const anchor = (e.target as HTMLElement).closest<HTMLAnchorElement>('a[href]')
      if (!anchor) return

      const { href } = anchor

      if (!isAllowed(href)) {
        e.preventDefault()
        setPendingUrl(href)
      }
    }

    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [isAllowed])

  const handleContinue = () => {
    if (!pendingUrl) return
    if (openInNewTab) {
      window.open(pendingUrl, '_blank', 'noopener')
    } else {
      window.location.href = pendingUrl
    }
    setPendingUrl(null)
  }

  return (
    <>
      {children}
      <ExternalRedirectModal
        open={Boolean(pendingUrl)}
        url={pendingUrl ?? ''}
        onCancel={() => setPendingUrl(null)}
        onContinue={handleContinue}
        data-testid="external-redirect-modal"
      />
    </>
  )
}

ExternalRedirectProvider.defaultProps = {
  whitelist: [window.location.hostname],
  openInNewTab: true
}
